/**
 * Ported from frontend/shared/src/commonMain/kotlin/sk/myslikrok/shared/Errors/.
 * A change to the Kotlin changes this file in the same commit — client code manifest §2a.
 *
 * Every failure the client can see, already mapped. No HTTP status reaches a screen.
 *
 * `message` is the API's own text wherever the API supplied one. The backend writes user-facing copy from
 * a reviewed catalogue that cannot contain the vocabulary BR-13 forbids; replacing it with a client-written
 * string would put that risk straight back.
 */
export type ApiErrorKind =
  'unauthenticated' | 'accessDenied' | 'notFound' | 'conflict' | 'invalidInput' | 'server' | 'network';

export type ApiError = {
  kind: ApiErrorKind;
  message: string;
  /** Present on `server` only. The same id appears in the server log line for this request. */
  correlationId?: string;
  /**
   * Present on `network` only: the device itself is offline, rather than the API being unreachable.
   *
   * Kept as a flag as well as a message because a screen does something with it that copy cannot — it
   * waits for `online` and re-submits (R0120, §5).
   */
  offline?: boolean;
};

export type ErrorBody = {
  statusCode?: number;
  message?: string;
  errorCode?: string;
  correlationId?: string;
};

export type ApiResult<T> = { ok: true; value: T } | { ok: false; error: ApiError };

/**
 * Maps a status and the API's error body onto the typed surface.
 *
 * Kept as a pure function rather than buried in the HTTP client so it can be tested exhaustively against
 * every status the backend documents — mirrors `ErrorMapping.kt`.
 */
export function mapApiError(statusCode: number, body: ErrorBody | null): ApiError {
  const message = body?.message?.trim() ? body.message : defaultMessageFor(statusCode);

  switch (statusCode) {
    case 401:
      return { kind: 'unauthenticated', message };
    case 403:
      return { kind: 'accessDenied', message };
    case 404:
      return { kind: 'notFound', message };
    case 409:
      return { kind: 'conflict', message };
    case 400:
    case 422:
      return { kind: 'invalidInput', message };
    default:
      return { kind: 'server', message, correlationId: body?.correlationId ?? '' };
  }
}

function defaultMessageFor(statusCode: number): string {
  switch (statusCode) {
    case 401:
      return 'Prihlásenie vypršalo.';
    case 403:
      return 'Na túto akciu nemáte oprávnenie.';
    case 404:
      return 'Nenašlo sa.';
    case 409:
      return 'Táto akcia sa už vykonala.';
    default:
      return statusCode >= 400 && statusCode <= 499
        ? 'Požiadavku sa nepodarilo spracovať.'
        : 'Na serveri nastala chyba.';
  }
}
